import {
  type BulkApproveStatusChangeRequestsRequest,
  type BulkApproveStatusChangeRequestsResponse,
  BulkApproveStatusChangeRequestsResponseSchema,
  type BulkAssignExitUnassignedRequest,
  type BulkAssignExitUnassignedResponse,
  BulkAssignExitUnassignedResponseSchema,
  type BulkReassignLeadsRequest,
  type BulkReassignLeadsResponse,
  BulkReassignLeadsResponseSchema,
  type CallLeadTarget,
  type CompleteFollowUpRequest,
  type CreateLeadRequest,
  type CreateStatusChangeRequestRequest,
  type DestinationChangeAssigneeOptionsResponse,
  DestinationChangeAssigneeOptionsResponseSchema,
  type BacklogDripConfig,
  BacklogDripConfigSchema,
  type BacklogDripRunResponse,
  BacklogDripRunResponseSchema,
  type BacklogDripSummaryResponse,
  BacklogDripSummaryResponseSchema,
  type UpdateBacklogDripConfigRequest,
  type ExitReleasePreview,
  ExitReleasePreviewSchema,
  type ExitUnassignedFormerOwnersResponse,
  ExitUnassignedFormerOwnersResponseSchema,
  type LeadDetail,
  LeadDetailSchema,
  type LeadListQuery,
  type LeadListResponse,
  LeadListResponseSchema,
  type LeadPipelineStripResponse,
  LeadPipelineStripResponseSchema,
  type LeadStatusChangeRequestListQuery,
  type LeadStatusChangeRequestListResponse,
  type LeadStatusChangeRequestPendingCount,
  type LeadStatusChangeRequestPendingCountQuery,
  LeadStatusChangeRequestListResponseSchema,
  LeadApprovalReviewerListResponseSchema,
  type LeadApprovalReviewerListResponse,
  LeadStatusChangeRequestPendingCountSchema,
  type NudgeStatusChangeRequestResponse,
  NudgeStatusChangeRequestResponseSchema,
  type ReviewStatusChangeRequestRequest,
  type ScheduleFollowUpRequest,
  type TransitionStageRequest,
  type UpdateLeadRequest,
} from '@utazzo/shared';

import type {
  WhatsAppCallPermission,
  WhatsAppTemplateView,
} from '@/features/whatsapp/whatsapp.api';

import { apiClient } from '@/lib/api-client';
import { extractServerError } from '@/lib/api-error';
import { runInChunks } from '@/lib/bulk-in-chunks';

/**
 * Leads admin API. Every response is validated with the shared Zod schema so
 * a server change cannot sneak past the UI types. Mirrors the server contract
 * in `apps/api/src/modules/leads/leads.controller.ts`:
 *   GET    /leads
 *   GET    /leads/:id
 *   POST   /leads
 *   PATCH  /leads/:id
 *   DELETE /leads/:id
 *   POST   /leads/:id/transition
 *   POST   /leads/:id/follow-ups
 *   POST   /leads/:id/follow-ups/:followUpId/complete
 *   POST   /leads/:id/follow-ups/:followUpId/cancel
 */

export const listLeads = async (query: LeadListQuery): Promise<LeadListResponse> => {
  const { data } = await apiClient.get<unknown>('/leads', { params: query });
  return LeadListResponseSchema.parse(data);
};

/**
 * Single-request pipeline tile counters. Replaces the 9-13 separate
 * `useLeadsList({ pageSize: 1, ...filter })` calls the strip used to
 * fire just to read `pagination.totalItems`. See
 * `docs/architecture/leads-pipeline-optimization-plan.md`.
 */
export const getPipelineStats = async (): Promise<LeadPipelineStripResponse> => {
  const { data } = await apiClient.get<unknown>('/leads/stats');
  return LeadPipelineStripResponseSchema.parse(data);
};

export const getLead = async (id: string): Promise<LeadDetail> => {
  const { data } = await apiClient.get<unknown>(`/leads/${id}`);
  return LeadDetailSchema.parse(data);
};

export const createLead = async (payload: CreateLeadRequest): Promise<LeadDetail> => {
  const { data } = await apiClient.post<unknown>('/leads', payload);
  return LeadDetailSchema.parse(data);
};

export const updateLead = async (id: string, payload: UpdateLeadRequest): Promise<LeadDetail> => {
  const { data } = await apiClient.patch<unknown>(`/leads/${id}`, payload);
  return LeadDetailSchema.parse(data);
};

export const deleteLead = async (id: string): Promise<void> => {
  await apiClient.delete(`/leads/${id}`);
};

// ----- Verb endpoints (atomic side-effects on the server) -----

export const transitionLeadStage = async (
  id: string,
  payload: TransitionStageRequest,
): Promise<LeadDetail> => {
  const { data } = await apiClient.post<unknown>(`/leads/${id}/transition`, payload);
  return LeadDetailSchema.parse(data);
};

export const scheduleFollowUp = async (
  id: string,
  payload: ScheduleFollowUpRequest,
): Promise<LeadDetail> => {
  const { data } = await apiClient.post<unknown>(`/leads/${id}/follow-ups`, payload);
  return LeadDetailSchema.parse(data);
};

export const completeFollowUp = async (
  id: string,
  followUpId: string,
  payload: CompleteFollowUpRequest,
): Promise<LeadDetail> => {
  const { data } = await apiClient.post<unknown>(
    `/leads/${id}/follow-ups/${followUpId}/complete`,
    payload,
  );
  return LeadDetailSchema.parse(data);
};

export const cancelFollowUp = async (id: string, followUpId: string): Promise<LeadDetail> => {
  const { data } = await apiClient.post<unknown>(
    `/leads/${id}/follow-ups/${followUpId}/cancel`,
    {},
  );
  return LeadDetailSchema.parse(data);
};

// ADR-0054: out-of-band status-change approval workflow.

export const createStatusChangeRequest = async (
  id: string,
  payload: CreateStatusChangeRequestRequest,
): Promise<LeadDetail> => {
  const { data } = await apiClient.post<unknown>(`/leads/${id}/status-change-requests`, payload);
  return LeadDetailSchema.parse(data);
};

export const approveStatusChangeRequest = async (
  id: string,
  requestId: string,
  payload: ReviewStatusChangeRequestRequest,
): Promise<LeadDetail> => {
  const { data } = await apiClient.post<unknown>(
    `/leads/${id}/status-change-requests/${requestId}/approve`,
    payload,
  );
  return LeadDetailSchema.parse(data);
};

/**
 * Who a pending DESTINATION_CHANGE request may be handed to on approval, each
 * flagged with whether they cover the requested destination. Server-scoped:
 * ADMIN every sales user; a SALES_LEAD their team plus the other team leads.
 */
export const getDestinationChangeAssigneeOptions = async (
  id: string,
  requestId: string,
): Promise<DestinationChangeAssigneeOptionsResponse> => {
  const { data } = await apiClient.get<unknown>(
    `/leads/${id}/status-change-requests/${requestId}/assignee-options`,
  );
  return DestinationChangeAssigneeOptionsResponseSchema.parse(data);
};

export const rejectStatusChangeRequest = async (
  id: string,
  requestId: string,
  payload: ReviewStatusChangeRequestRequest,
): Promise<LeadDetail> => {
  const { data } = await apiClient.post<unknown>(
    `/leads/${id}/status-change-requests/${requestId}/reject`,
    payload,
  );
  return LeadDetailSchema.parse(data);
};

/**
 * Admin-only bulk approve for the pending-approvals table's multi-select bar.
 * No bulk reject counterpart - rejection needs a per-item reason, so that
 * stays a one-at-a-time action through `rejectStatusChangeRequest`.
 */
export const bulkApproveStatusChangeRequests = async (
  payload: BulkApproveStatusChangeRequestsRequest,
): Promise<BulkApproveStatusChangeRequestsResponse> => {
  const { data } = await apiClient.post<unknown>(
    '/leads/status-change-requests/bulk-approve',
    payload,
  );
  return BulkApproveStatusChangeRequestsResponseSchema.parse(data);
};

/**
 * 2026-07-10. Requester-side cancel. The API's own guard checks
 * that the caller is the original `requestedById`; anyone else gets
 * 403 so the UI just wires the button unconditionally on the banner
 * and trusts the server to reject if the user shouldn't be able to.
 */
export const cancelStatusChangeRequest = async (
  id: string,
  requestId: string,
): Promise<LeadDetail> => {
  const { data } = await apiClient.post<unknown>(
    `/leads/${id}/status-change-requests/${requestId}/cancel`,
    {},
  );
  return LeadDetailSchema.parse(data);
};

/** Sales-agent nudge - reminds the assigned reviewer about a PENDING request. */
export const nudgeStatusChangeRequest = async (
  id: string,
  requestId: string,
): Promise<NudgeStatusChangeRequestResponse> => {
  const { data } = await apiClient.post<unknown>(
    `/leads/${id}/status-change-requests/${requestId}/nudge`,
    {},
  );
  return NudgeStatusChangeRequestResponseSchema.parse(data);
};

export const listStatusChangeRequests = async (
  query: LeadStatusChangeRequestListQuery,
): Promise<LeadStatusChangeRequestListResponse> => {
  const { data } = await apiClient.get<unknown>('/leads/status-change-requests', {
    params: query,
  });
  return LeadStatusChangeRequestListResponseSchema.parse(data);
};

export const listStatusChangeRequestReviewers =
  async (): Promise<LeadApprovalReviewerListResponse> => {
    const { data } = await apiClient.get<unknown>('/leads/status-change-requests/reviewers');
    return LeadApprovalReviewerListResponseSchema.parse(data);
  };

/**
 * Pending queue depth. `requestedById` narrows it to one team member's
 * submissions - the Daily Tracker's "Pending Approvals" tile passes the
 * inbox owner filter through here so the tile agrees with the approvals page
 * it drills into.
 */
export const getPendingStatusChangeRequestCount = async (
  query: LeadStatusChangeRequestPendingCountQuery = {},
): Promise<LeadStatusChangeRequestPendingCount> => {
  const { data } = await apiClient.get<unknown>('/leads/status-change-requests/pending-count', {
    params: query,
  });
  return LeadStatusChangeRequestPendingCountSchema.parse(data);
};

export const bulkAssignExitUnassigned = async (
  payload: BulkAssignExitUnassignedRequest,
): Promise<BulkAssignExitUnassignedResponse> => {
  const { data } = await apiClient.post<unknown>('/leads/exit-unassigned/bulk-assign', payload);
  return BulkAssignExitUnassignedResponseSchema.parse(data);
};

/**
 * Reassign a selection of leads to one agent (inbox aside -> Select -> Assign).
 * Sent in small chunks (see runInChunks) and folded back into one result.
 */
export const bulkReassignLeads = async (
  payload: BulkReassignLeadsRequest,
  onProgress?: (done: number, total: number) => void,
): Promise<BulkReassignLeadsResponse> => {
  const run = await runInChunks(
    payload.ids,
    async (ids) => {
      const { data } = await apiClient.post<unknown>('/leads/bulk-reassign', { ...payload, ids });
      return BulkReassignLeadsResponseSchema.parse(data);
    },
    {
      ...(onProgress ? { onProgress } : {}),
      errorText: (err) => extractServerError(err).message ?? 'Request failed',
    },
  );
  return {
    updated: run.results.reduce((n, r) => n + r.updated, 0),
    unchanged: run.results.reduce((n, r) => n + r.unchanged, 0),
    failed: [...run.results.flatMap((r) => r.failed), ...run.unsent],
  };
};

/**
 * Former owners with leads in the exit-unassigned queue, busiest first. Feeds
 * the queue's "Former owner" filter - departed users are in no agent picker.
 */
export const getExitUnassignedFormerOwners =
  async (): Promise<ExitUnassignedFormerOwnersResponse> => {
    const { data } = await apiClient.get<unknown>('/leads/exit-unassigned/former-owners');
    return ExitUnassignedFormerOwnersResponseSchema.parse(data);
  };

/**
 * Backlog drip: how the exit-unassigned queue is being dealt out (what is
 * left by priority, today's quota and who got what) plus its settings.
 */
export const getBacklogDrip = async (): Promise<BacklogDripSummaryResponse> => {
  const { data } = await apiClient.get<unknown>('/leads/exit-unassigned/backlog-drip');
  return BacklogDripSummaryResponseSchema.parse(data);
};

export const updateBacklogDrip = async (
  patch: UpdateBacklogDripConfigRequest,
): Promise<BacklogDripConfig> => {
  const { data } = await apiClient.patch<unknown>('/leads/exit-unassigned/backlog-drip', patch);
  return BacklogDripConfigSchema.parse(data);
};

/** One pass right now, whatever the clock or the switch says. Admin only. */
export const runBacklogDrip = async (): Promise<BacklogDripRunResponse> => {
  const { data } = await apiClient.post<unknown>('/leads/exit-unassigned/backlog-drip/run');
  return BacklogDripRunResponseSchema.parse(data);
};

/**
 * Preview how many leads would release into the exit-unassigned queue if the
 * given user exits. Drives the deactivate / delete / date-of-exit confirm
 * popup. Server enforces RBAC (ADMIN any user; SALES_LEAD self + reports).
 */
export const getExitReleasePreview = async (userId: string): Promise<ExitReleasePreview> => {
  const { data } = await apiClient.get<unknown>(`/leads/exit-preview/${userId}`);
  return ExitReleasePreviewSchema.parse(data);
};

/**
 * CallerDesk click-to-call: rings the logged-in agent first, then bridges the
 * lead. Server contract: `apps/api/src/modules/callerdesk/callerdesk.controller.ts`.
 * Response is a provider acknowledgement (no shared schema - passthrough).
 */
export interface CallLeadResponse {
  leadId: string;
  target: CallLeadTarget;
  dialedNumber: string;
  memberId: string;
  memberName: string;
  provider: string;
  providerResponse: unknown;
}

export const callLead = async (
  id: string,
  target: CallLeadTarget = 'PRIMARY',
): Promise<CallLeadResponse> => {
  const { data } = await apiClient.post<CallLeadResponse>(`/leads/${id}/call`, { target });
  return data;
};

/**
 * One CallerDesk CDR row, mirroring `LeadCallLogEntry` in
 * `apps/api/src/modules/callerdesk/callerdesk.service.ts`. Deliberately
 * exhaustive - the Calls tab is the "show everything CallerDesk reports"
 * surface; columns get trimmed once ops decides what matters.
 */
export interface LeadCallLogEntry {
  providerRowId: string;
  callSid: string;
  recordingUrl: string | null;
  deskphone: string | null;
  callerName: string | null;
  customerNumber: string | null;
  memberName: string | null;
  memberNumber: string | null;
  coinsDeducted: number | null;
  callDate: string | null;
  startTime: string | null;
  endTime: string | null;
  totalDurationSeconds: number | null;
  talkDurationSeconds: number | null;
  ringingDurationSeconds: number | null;
  circle: string | null;
  keyPressed: string | null;
  blocked: string | null;
  callResult: string | null;
  callStatus: string | null;
  callGroupName: string | null;
  legAPickedTime: string | null;
  legBStartTime: string | null;
  legBPickedTime: string | null;
  flowType: string | null;
  memberType: string | null;
}

export interface LeadCallLogResponse {
  leadId: string;
  numbersQueried: string[];
  calls: LeadCallLogEntry[];
}

/** Live CallerDesk call history for a lead (no local storage). */
export const listLeadCalls = async (id: string): Promise<LeadCallLogResponse> => {
  const { data } = await apiClient.get<LeadCallLogResponse>(`/leads/${id}/calls`);
  return data;
};

// ----- WhatsApp chat (DoubleTick) -----
// Server contract: apps/api/src/modules/doubletick/whatsapp-chat.controller.ts
// (docs/integrations/doubletick-whatsapp.md). Passthrough types - no
// shared Zod schema yet; the payload shapes are still settling while media
// and status events get captured.

export interface LeadWhatsAppMessage {
  id: string;
  direction: 'IN' | 'OUT';
  kind: string; // TEXT | IMAGE | DOCUMENT | AUDIO | VIDEO | TEMPLATE | OTHER
  body: string | null;
  mediaUrl: string | null;
  templateName: string | null;
  templateView?: WhatsAppTemplateView | null;
  status: string; // RECEIVED | QUEUED | SENT | DELIVERED | READ | FAILED
  errorMessage: string | null;
  sentById: string | null;
  sentAt: string | null;
  createdAt: string;
}

/** Which of the customer's numbers a lead's WhatsApp thread is on. */
export type LeadPhoneTarget = 'PRIMARY' | 'ALTERNATE';

export interface LeadWhatsAppThread {
  leadId: string;
  phone: string | null;
  /** The number this thread is actually on (falls back when one is missing). */
  target: LeadPhoneTarget;
  /** Both numbers on file, E.164 digits - what the widget's picker offers. */
  phones: { primary: string | null; alternate: string | null };
  conversation: {
    id: string;
    phone: string;
    contactName: string | null;
    lastInboundAt: string | null;
    lastMessageAt: string | null;
  } | null;
  messages: LeadWhatsAppMessage[];
  windowOpen: boolean;
  windowExpiresAt: string | null;
  /** The last send to this number bounced as undeliverable (not on WhatsApp). */
  notOnWhatsApp?: boolean;
  callPermission?: WhatsAppCallPermission | null;
}

export const getLeadWhatsAppThread = async (
  id: string,
  target: LeadPhoneTarget = 'PRIMARY',
): Promise<LeadWhatsAppThread> => {
  const { data } = await apiClient.get<LeadWhatsAppThread>(`/leads/${id}/whatsapp`, {
    params: { target },
  });
  return data;
};

export interface SendLeadWhatsAppMessagePayload {
  text?: string;
  templateName?: string;
  templateParams?: string[];
  buttonParams?: string[];
  attachment?: {
    s3Key: string;
    kind: 'IMAGE' | 'VIDEO' | 'AUDIO' | 'DOCUMENT';
    filename?: string;
    caption?: string;
  };
  location?: { latitude: number; longitude: number; name?: string; address?: string };
}

export const sendLeadWhatsAppMessage = async (
  id: string,
  payload: SendLeadWhatsAppMessagePayload,
  target: LeadPhoneTarget = 'PRIMARY',
): Promise<LeadWhatsAppMessage> => {
  const { data } = await apiClient.post<LeadWhatsAppMessage>(
    `/leads/${id}/whatsapp/messages`,
    payload,
    { params: { target } },
  );
  return data;
};
